import React from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Button,
} from "@mui/material";

import {
  Person,
  Phone,
  CorporateFare,
  LocationOn,
  ArrowBack,
  AccessTime,
  Comment,
  History,
} from "@mui/icons-material";

import { useNavigate } from 'react-router-dom';

import { useParams } from "react-router-dom";

import { useGetShipmentByIdQuery } from "store/services/publicApi";

import NavBar from "components/navbar";

import { LandingFooter } from "./LandingFooter";
import InfoGridSkeleton from "components/Shared/Skeleton/InfoGridSkeleton";

const shipmentSteps = ["Pending", "Collected", "Loaded", "Delivered"];

// Map all possible statuses to the last completed step in shipmentSteps
const statusToStepMapping = {
  Pending: 0,
  Collected: 1,
  Consolidation: 1,
  Loaded: 2,
  Unloaded: 2,
  Deconsolidation: 2,
  CrossDocked: 2,
  SendOut: 2,
  Warehousing: 2,
  Delivered: 3,
  DeliveryFailed: 2,
  Return: 2,
  Cancelled: 0,
};

const getCurrentStepIndex = (status) => {
  if (!status) return 0;
  const stepIndex = statusToStepMapping[status] ?? 0;
  return stepIndex;
};

const InfoRow = ({ icon, text }) => (
  <Box display="flex" alignItems="center" ml={1} gap={1} my={1}>
    {icon}
    <Typography variant="body3" color="text.primary">
      {text || "—"}
    </Typography>
  </Box>
);

export const ViewShipmentDetailByStatus = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { data: shipmentData,
    isLoading: loadingShipment,
    isFetching: fetchingShipment,
    isError
  } = useGetShipmentByIdQuery(id);

  const isBusy = loadingShipment || fetchingShipment
  if (isBusy) return <InfoGridSkeleton />

  return (
    <>
      <NavBar />
      <Box sx={{ minHeight: "59vh", bgcolor: "background.default", p: 4, m: 6 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Button
            variant="contained"
            startIcon={<ArrowBack />}
            onClick={() => navigate(-1)}
            sx={{
              bgcolor: "crimson",
              color: "white",
              "&:hover": { bgcolor: "darkred" },
              height: "40px",
              ml: '152px', mb: '10px'
            }}
          >
            Back
          </Button>
        </Box>
        <Box sx={{ maxWidth: 1100, mx: "auto" }}>
          {isError && (
            <Alert severity="error">
              <Typography fontWeight={600} color="red">
                Shipment Not Found, Check your Shipment ID or Reference
              </Typography>
            </Alert>
          )}

          {shipmentData && (
            <Card elevation={3}>
              <CardContent>
                <Grid container justifyContent="space-between" alignItems="center">
                  <Typography variant="h6" gutterBottom color="primary.main">
                    {shipmentData.shipmentId ? `Shipment ID: ${shipmentData.shipmentId}` : ""}
                    {shipmentData.shipmentId && shipmentData.reference ? " | " : ""}
                    {shipmentData.reference ? `Reference: ${shipmentData.reference}` : ""}
                  </Typography>
                </Grid>
                {/* Shipment Progress */}
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Shipment Progress :
                </Typography>
                <Stepper
                  activeStep={getCurrentStepIndex(shipmentData?.status)}
                  alternativeLabel
                >
                  {shipmentSteps.map((label, index) => {
                    const currentStepIndex = getCurrentStepIndex(shipmentData?.status);
                    const isCompleted = currentStepIndex >= index;
                    const isActive = currentStepIndex === index;

                    return (
                      <Step key={label} completed={isCompleted}>
                        <StepLabel
                          sx={{
                            ...(isActive || isCompleted
                              ? {
                                color: "success.main",
                                ".MuiStepIcon-root": {
                                  color: "primary.main",
                                },
                              }
                              : {}),
                          }}
                        >
                          {label}
                        </StepLabel>
                      </Step>
                    );
                  })}
                </Stepper>

                {/* Sender/Receiver Info */}
                <Grid container spacing={2} sx={{ mt: 2 }}>
                  <Grid item xs={12}>
                    <Box p={3} borderRadius={2} bgcolor="grey.100" boxShadow={1} ml={0}>
                      <Grid container spacing={2}>
                        {/* Sender */}
                        <Grid item xs={12} md={6}>
                          <Typography
                            ml={1}
                            variant="h8"
                            color="primary"
                            gutterBottom
                            sx={{
                              fontWeight: "bold",
                              textTransform: "uppercase",
                              letterSpacing: 1,
                            }}
                          >
                            Sender ({shipmentData.vendor ? "Vendor" : "Customer"}):
                          </Typography>
                          {shipmentData.vendor ? (
                            <>
                              <InfoRow icon={<Person sx={{ color: 'red' }} />} text={shipmentData.vendor?.ownerName || shipmentData.vendor?.shopName} />
                              <InfoRow icon={<Phone sx={{ color: 'red' }} />} text={shipmentData.vendor?.phone} />
                              <InfoRow icon={<LocationOn sx={{ color: 'red' }} />} text={shipmentData.vendor?.address} />
                              <InfoRow
                                icon={<CorporateFare sx={{ color: 'red' }} />}
                                text={`${shipmentData.sourceBranch?.name} (${shipmentData.sourceBranch?.code})`}
                              />
                            </>
                          ) : (
                            <>
                              <InfoRow icon={<Person sx={{ color: 'red' }} />} text={shipmentData.customerId?.customerUser?.name} />
                              <InfoRow icon={<Phone sx={{ color: 'red' }} />} text={shipmentData.customerId?.customerUser?.phone} />
                              <InfoRow icon={<LocationOn sx={{ color: 'red' }} />} text={shipmentData.customerId?.address} />
                              <InfoRow icon={<CorporateFare sx={{ color: 'red' }} />}
                                text={`${shipmentData.sourceBranch?.name} (${shipmentData.sourceBranch?.code})`}
                              />
                            </>
                          )}
                        </Grid>

                        {/* Receiver */}
                        <Grid item xs={12} md={6}>
                          <Typography
                            ml={1}
                            variant="h8"
                            color="primary"
                            gutterBottom
                            sx={{
                              fontWeight: "bold",
                              textTransform: "uppercase",
                              letterSpacing: 1,
                            }}
                          >
                            Receiver:
                          </Typography>
                          <InfoRow icon={<Person sx={{ color: 'red' }} />} text={shipmentData.receiver?.customerUser?.name} />
                          <InfoRow icon={<Phone sx={{ color: 'red' }} />} text={shipmentData.receiver?.customerUser?.phone} />
                          <InfoRow icon={<LocationOn sx={{ color: 'red' }} />} text={shipmentData.receiver?.address} />
                          <InfoRow icon={<CorporateFare sx={{ color: 'red' }} />}
                            text={`${shipmentData.destinationBranch?.name} (${shipmentData.destinationBranch?.code})`}
                          />
                        </Grid>
                      </Grid>
                    </Box>

                    <Box mt={4} p={5} borderRadius={2} bgcolor="grey.100" boxShadow={1} >
                      <Typography
                        variant="h6"
                        color="primary"
                        gutterBottom
                        sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, }}
                      >
                        <History fontSize="small" sx={{ fontSize: '30px' }} />
                        Shipment History
                      </Typography>

                      <Box sx={{
                        position: 'relative',
                        pl: 3,
                        '&:before': {
                          content: '""',
                          position: 'absolute',
                          left: '15px',
                          top: 0,
                          bottom: 0,
                          width: '2px',
                          bgcolor: 'primary.main',
                        }
                      }}>
                        {shipmentData?.history?.map((item, index) => (
                          <Box
                            key={item?._id}
                            sx={{
                              mb: 3,
                              position: 'relative',
                              '&:last-child': { mb: 0 }
                            }}
                          >
                            {/* Timeline dot */}
                            <Box sx={{
                              position: 'absolute',
                              left: '-16px',
                              top: '8px',
                              width: '16px',
                              height: '16px',
                              borderRadius: '50%',
                              bgcolor: 'primary.main',
                              zIndex: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              '&:after': {
                                content: '""',
                                width: '8px',
                                height: '8px',
                                bgcolor: 'background.paper',
                                borderRadius: '50%'
                              }
                            }} />

                            {/* Content */}
                            <Typography
                              variant="body1"
                              fontWeight={500}
                              sx={{
                                mb: 1,
                                ml: 2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                color: 'text.primary'
                              }}
                            >
                              <Comment fontSize="small" color="primary" />
                              {item?.comment}
                            </Typography>

                            <Box sx={{
                              display: 'flex',
                              gap: 2,
                              alignItems: 'center',
                              color: 'text.secondary',
                              flexWrap: 'wrap'
                            }}>
                              <AccessTime fontSize="small" sx={{ color: 'text.secondary', fontSize: '20px', ml: 2 }} />
                              <Typography variant="body2" sx={{ fontSize: "15px" }}>
                                {new Date(item?.createdAt)?.toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}
        </Box>
      </Box>
      <LandingFooter />
    </>
  );
};